import { Moon, SunMedium } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import "./TvFrame.css";

/* Light/dark mode for the embedded TV renderer (/tv-preview/preview.html).
 * Contract v2.1 section 1: URL param `theme=light|dark` on first load, then
 * `{ type: "mrp:theme", theme }` postMessage to switch live without a refetch.
 *
 * The choice is a per-user preference (localStorage, default LIGHT) shared by
 * every place that embeds the renderer -- the designer canvas, the report
 * workspace preview and the viewer -- so previewing dark in one place keeps it
 * dark everywhere until switched back. */

export type PreviewTheme = "light" | "dark";

const KEY = "mrp.previewTheme";
const EVENT = "mrp:preview-theme";

export function readPreviewTheme(): PreviewTheme {
  try {
    return window.localStorage.getItem(KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

/** Colours of the area around the renderer's stage, per mode. */
export const PREVIEW_STAGE_BG: Record<PreviewTheme, string> = { light: "#e9eef4", dark: "#050d1a" };
export const PREVIEW_SKELETON_BG: Record<PreviewTheme, string> = { light: "#d5dde8", dark: "#12243d" };

export function usePreviewTheme(): [PreviewTheme, (t: PreviewTheme) => void] {
  const [theme, setThemeState] = useState<PreviewTheme>(readPreviewTheme);
  useEffect(() => {
    const sync = () => setThemeState(readPreviewTheme());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  const setTheme = useCallback((t: PreviewTheme) => {
    try {
      window.localStorage.setItem(KEY, t);
    } catch {
      /* private mode: still switch for this page */
    }
    setThemeState(t);
    window.dispatchEvent(new Event(EVENT));
  }, []);
  return [theme, setTheme];
}

/** Colour mode for a PUBLISHED view (viewer page, share link). Unlike usePreviewTheme this never reads
 * the per-browser preference, so a report looks the same on every machine: `?theme=light|dark` in the
 * URL, else the report's own default (`settings.defaultTheme`), else light (what the studio shows).
 * The viewer's toggle only changes this page view. */
export function useViewerTheme(reportDefault?: unknown): [PreviewTheme, (t: PreviewTheme) => void] {
  const fromUrl = (() => {
    try {
      const v = new URLSearchParams(window.location.search).get("theme");
      return v === "dark" || v === "light" ? v : null;
    } catch {
      return null;
    }
  })();
  const [choice, setChoice] = useState<PreviewTheme | null>(fromUrl);
  const theme: PreviewTheme = choice ?? (reportDefault === "dark" ? "dark" : "light");
  return [theme, setChoice];
}

/** Two-button Light / Dark switch. `compact` = icons only (for overlays). */
export function PreviewThemeToggle({ theme, onChange, compact, label = "Preview" }: { theme: PreviewTheme; onChange: (t: PreviewTheme) => void; compact?: boolean; label?: string }) {
  return (
    <div className={`st-tvt${compact ? " st-tvt--compact" : ""}`} role="group" aria-label={`${label} colour mode`} title="How the report looks on a light or a dark screen. Only changes this preview, not the report.">
      {!compact ? <span className="st-tvt__label">{label}</span> : null}
      <button type="button" className="st-tvt__btn" aria-pressed={theme === "light"} onClick={() => onChange("light")} aria-label="Light">
        <SunMedium size={14} />
        {!compact ? "Light" : null}
      </button>
      <button type="button" className="st-tvt__btn" aria-pressed={theme === "dark"} onClick={() => onChange("dark")} aria-label="Dark">
        <Moon size={14} />
        {!compact ? "Dark" : null}
      </button>
    </div>
  );
}
